// BaseTrigger.ts
// Base class for all triggers — mirrors ConneXt v2 ActionTrigger + BaseTimedTrigger logic.
import { ActionRepository } from "./ActionRepository";
import { getCurrentUrlWithoutParams, addTimeIntervalToDate } from "../../../../core/utils/Utils";
import { customTime } from "../../../../core/utils/CustomTime";
import { TimeRepeatableActionsCS } from "../../../../data/constants/Constants";
export class BaseTrigger {
    constructor(actionInstance, action) {
        this.actionInstance = actionInstance;
        this.action = action;
    }
    get when() {
        var _a, _b;
        return ((_a = this.action) === null || _a === void 0 ? void 0 : _a.when) || ((_b = this.action) === null || _b === void 0 ? void 0 : _b.When) || {};
    }
    get actionId() {
        var _a, _b;
        return ((_a = this.action) === null || _a === void 0 ? void 0 : _a.actionId) || ((_b = this.action) === null || _b === void 0 ? void 0 : _b.Id) || "";
    }
    get conversationId() {
        var _a, _b;
        return ((_a = this.action) === null || _a === void 0 ? void 0 : _a.conversationId) || ((_b = this.action) === null || _b === void 0 ? void 0 : _b.ConversationId) || "";
    }
    get repeatableOnConversation() {
        var _a;
        return (_a = this.when.RepeatableOnConversation) !== null && _a !== void 0 ? _a : 0;
    }
    get repeatableOnArticle() {
        var _a;
        return (_a = this.when.RepeatableOnArticle) !== null && _a !== void 0 ? _a : 0;
    }
    get repeatAfter() {
        var _a;
        return (_a = this.when.RepeatAfter) !== null && _a !== void 0 ? _a : 0;
    }
    get delay() {
        var _a;
        return (_a = this.when.Delay) !== null && _a !== void 0 ? _a : 0;
    }
    // ──────────────────────────────────────────────
    // Repeatable on Conversation (matches ConneXt v2 ActionTrigger.canRepeatableOnConversation)
    // ──────────────────────────────────────────────
    canRepeatableOnConversation(repeatable) {
        const limit = repeatable !== null && repeatable !== void 0 ? repeatable : this.repeatableOnConversation;
        // 0 means unlimited
        if (limit === 0) {
            return true;
        }
        const actionInfo = this.getConversationActionInfo();
        if (!actionInfo) {
            return true;
        }
        return actionInfo < limit;
    }
    getConversationActionInfo() {
        const repeatInConv = ActionRepository.getRepeatablesInConv();
        if (repeatInConv && repeatInConv[this.actionId] !== undefined) {
            return repeatInConv[this.actionId];
        }
        return null;
    }
    setConversationActionInfo() {
        const repeatInConv = ActionRepository.getRepeatablesInConv();
        const actionInfo = this.getConversationActionInfo();
        if (actionInfo !== null) {
            repeatInConv[this.actionId] = actionInfo + 1;
        }
        else {
            repeatInConv[this.actionId] = 1;
        }
        ActionRepository.setRepeatablesInConv(repeatInConv);
        this.setArticleActionInfo();
    }
    // ──────────────────────────────────────────────
    // Repeatable on Article (matches ConneXt v2 ActionTrigger.canRepeatableOnArticle)
    // ──────────────────────────────────────────────
    canRepeatableOnArticle(repeatable) {
        const limit = repeatable !== null && repeatable !== void 0 ? repeatable : this.repeatableOnArticle;
        // 0 means unlimited
        if (limit === 0) {
            return true;
        }
        const repeats = ActionRepository.getRepeatablesInArticle();
        const currentUrl = getCurrentUrlWithoutParams();
        if (!repeats || repeats.url !== currentUrl) {
            return true;
        }
        return repeats.count < limit;
    }
    setArticleActionInfo() {
        let repeats = ActionRepository.getRepeatablesInArticle();
        const currentUrl = getCurrentUrlWithoutParams();
        if (!repeats || repeats.url !== currentUrl) {
            repeats = {
                url: currentUrl,
                count: 1
            };
        }
        else {
            repeats.count++;
        }
        ActionRepository.setRepeatablesInArticle(repeats);
    }
    // ──────────────────────────────────────────────
    // Time-based RepeatAfter (matches ConneXt v2 BaseTimedTrigger)
    // ──────────────────────────────────────────────
    canTimeRepeat() {
        if (this.repeatAfter === 0) {
            // No time-based repeat restriction
            return true;
        }
        const repeatableData = ActionRepository.getTimeRepeatableActions();
        if (!this.issetTimeActionData(repeatableData)) {
            return true;
        }
        const actionData = repeatableData[this.conversationId][this.actionId];
        const repeatDate = actionData[TimeRepeatableActionsCS.RepeatAfter];
        const count = actionData[TimeRepeatableActionsCS.Count];
        // Check if repeat time has passed
        if (customTime.get() > new Date(repeatDate)) {
            // Time is up, also check conversation limit
            if (this.repeatableOnConversation === 0) {
                return true;
            }
            return count < this.repeatableOnConversation;
        }
        return false;
    }
    setTimeRepeatableInfo() {
        const repeatableData = ActionRepository.getTimeRepeatableActions();
        const existingData = this.issetTimeActionData(repeatableData)
            ? repeatableData[this.conversationId][this.actionId]
            : null;
        let newData = {};
        newData[TimeRepeatableActionsCS.RepeatAfter] = addTimeIntervalToDate(this.repeatAfter);
        if (!repeatableData[this.conversationId]) {
            repeatableData[this.conversationId] = {};
        }
        if (existingData) {
            newData[TimeRepeatableActionsCS.Count] = existingData[TimeRepeatableActionsCS.Count] + 1;
        }
        else {
            newData[TimeRepeatableActionsCS.Count] = 1;
        }
        repeatableData[this.conversationId][this.actionId] = newData;
        ActionRepository.setTimeRepeatableActions(repeatableData);
    }
    issetTimeActionData(repeatableData) {
        return (repeatableData &&
            repeatableData[this.conversationId] &&
            repeatableData[this.conversationId][this.actionId]);
    }
    // ──────────────────────────────────────────────
    // Combined canTrigger check
    // ──────────────────────────────────────────────
    canTrigger() {
        // Time-based triggers use RepeatAfter + RepeatableOnConversation
        if (this.repeatAfter > 0) {
            return this.canTimeRepeat();
        }
        // Non-timed triggers use RepeatableOnConversation + RepeatableOnArticle
        if (!this.canRepeatableOnConversation()) {
            return false;
        }
        if (!this.canRepeatableOnArticle()) {
            return false;
        }
        return true;
    }
    // ──────────────────────────────────────────────
    // Trigger execution
    // ──────────────────────────────────────────────
    markTriggered() {
        if (this.repeatAfter > 0) {
            this.setTimeRepeatableInfo();
        }
        else {
            this.setConversationActionInfo();
        }
    }
    startTimer(callback) {
        if (this.delay === 0) {
            callback();
        }
        else {
            this.timer = setTimeout(() => {
                callback();
            }, this.delay);
        }
    }
    executeWithDelay() {
        if (!this.canTrigger())
            return;
        this.startTimer(() => {
            this.markTriggered();
            this.actionInstance.execute();
        });
    }
    unRegister() {
        if (this.timer) {
            clearTimeout(this.timer);
            this.timer = null;
        }
    }
    resetRepeatableData() {
        // Reset conversation repeatable
        const repeatInConv = ActionRepository.getRepeatablesInConv();
        if (repeatInConv[this.actionId]) {
            delete repeatInConv[this.actionId];
            ActionRepository.setRepeatablesInConv(repeatInConv);
        }
        // Reset article repeatable
        ActionRepository.removeRepeatablesInArticle();
        // Reset time repeatable
        const timeData = ActionRepository.getTimeRepeatableActions();
        if (timeData[this.conversationId] && timeData[this.conversationId][this.actionId]) {
            delete timeData[this.conversationId][this.actionId];
            ActionRepository.setTimeRepeatableActions(timeData);
        }
    }
}
